/**
 * Tour Options Component
 * Renders clickable section buttons in the chat interface
 */

import React from 'react';
import styled from 'styled-components';
import { TOUR_SECTIONS } from '../../utils/interactiveTour';

const OptionsContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 20px;
    margin-top: 12px;
    align-items: center;
    
    @media (max-width: 480px) {
        gap: 16px;
    }
`;

const ActionsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 12px;
    padding: 0 4px;
`;

const OptionButton = styled.button`
    background: ${props => props.color}15;
    border: 1.5px solid ${props => props.color};
    padding: 6px 12px;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    color: ${props => props.color};
    text-decoration: none;
    
    &:hover {
        background: ${props => props.color}25;
        transform: translateY(-2px);
        box-shadow: 0 4px 12px ${props => props.color}40;
    }
    
    &:active {
        transform: translateY(0);
        box-shadow: 0 2px 6px ${props => props.color}30;
    }
    
    @media (max-width: 480px) {
        font-size: 12px;
        padding: 5px 10px;
    }
`;

const ActionButton = styled.button`
    padding: 12px 16px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    border: 1px solid rgba(255, 255, 255, 0.15);
    background: rgba(255, 255, 255, 0.03);
    text-align: left;
    
    ${props => props.variant === 'continue' && `
        border-color: rgba(52, 211, 153, 0.3);
        color: #34D399;
        background: rgba(52, 211, 153, 0.03);
        
        &:hover {
            background: rgba(52, 211, 153, 0.08);
            border-color: rgba(52, 211, 153, 0.4);
            color: #34D399;
            transform: translateX(2px);
        }
    `}
    
    ${props => props.variant === 'menu' && `
        border-color: rgba(255, 255, 255, 0.15);
        color: rgba(255, 255, 255, 0.85);
        background: rgba(255, 255, 255, 0.03);
        
        &:hover {
            background: rgba(255, 255, 255, 0.08);
            border-color: rgba(255, 35, 0, 0.3);
            color: #FFFFFF;
            transform: translateX(2px);
        }
    `}
    
    ${props => props.variant === 'done' && `
        border-color: rgba(255, 35, 0, 0.3);
        color: #FF2300;
        background: rgba(255, 35, 0, 0.05);
        
        &:hover {
            background: rgba(255, 35, 0, 0.1);
            border-color: rgba(255, 35, 0, 0.4);
            transform: translateX(2px);
        }
    `}

    ${props => props.variant === 'explore' && `
        border-color: rgba(255, 149, 0, 0.3);
        color: #FF9500;
        background: rgba(255, 149, 0, 0.05);
        
        &:hover {
            background: rgba(255, 149, 0, 0.1);
            border-color: rgba(255, 149, 0, 0.4);
            color: #FF9500;
        }
    `}
    
    &:active {
        transform: scale(0.98);
    }
    
    @media (max-width: 480px) {
        padding: 10px 14px;
        font-size: 12px;
    }
`;

export const TourOptions = ({ options, onOptionSelect }) => {
    if (!options || options.length === 0) return null;

    const renderOption = (option) => {
        // Handle action buttons
        if (option === 'continue') {
            return (
                <ActionButton
                    key="continue"
                    variant="continue"
                    onClick={() => onOptionSelect('continue')}
                >
                    Continue to next section →
                </ActionButton>
            );
        }
        
        if (option === 'menu') {
            return (
                <ActionButton
                    key="menu"
                    variant="menu"
                    onClick={() => onOptionSelect('menu')}
                >
                    ↺ Reload menu
                </ActionButton>
            );
        }
        
        if (option === 'done') {
            return (
                <ActionButton
                    key="done"
                    variant="done"
                    onClick={() => onOptionSelect('done')}
                >
                    ✓ I'm ready to start using DIZEE!
                </ActionButton>
            );
        }

        if (option === 'Explore More') {
            return (
                <ActionButton
                    key="explore"
                    variant="explore"
                    onClick={() => onOptionSelect('Explore More')}
                >
                    🌟 Explore More Apps
                </ActionButton>
            );
        }
        
        // Handle section buttons
        const section = TOUR_SECTIONS[option];
        if (section) {
            return (
                <OptionButton
                    key={option}
                    color={section.color}
                    onClick={() => onOptionSelect(option)}
                >
                    {section.name}
                </OptionButton>
            );
        }
        
        return null;
    };

    // Separate section options from action buttons
    const sectionOptions = options.filter(opt => TOUR_SECTIONS[opt]);
    const actionOptions = options.filter(opt => !TOUR_SECTIONS[opt]);

    return (
        <>
            {sectionOptions.length > 0 && (
                <OptionsContainer>
                    {sectionOptions.map(renderOption)}
                </OptionsContainer>
            )}
            {actionOptions.length > 0 && (
                <ActionsContainer>
                    {actionOptions.map(renderOption)}
                </ActionsContainer>
            )}
        </>
    );
};

export default TourOptions;


